import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  twitterCard = 'summary_large_image',
  noIndex = false,
  structuredData,
  lang = 'de',
  alternateUrls = []
}) => {
  const siteName = 'Sentmo';
  const defaultDescription = lang === 'de'
    ? 'Sentmo - Trading Sentiment Analyse fuer private Trader. Taegliche Sentiment-Daten fuer US30, NASDAQ, S&P500, DAX und Gold.'
    : 'Sentmo - Trading Sentiment Analysis for retail traders. Daily sentiment data for US30, NASDAQ, S&P500, DAX and Gold.';
  const defaultImage = 'https://sentmo.com/og-image.jpg';
  const baseUrl = 'https://sentmo.com';

  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} - Trading Sentiment Analyse`;
  const metaDescription = description || defaultDescription;
  const metaImage = ogImage || defaultImage;
  const canonical = canonicalUrl || baseUrl;
  const ogLocale = lang === 'de' ? 'de_DE' : 'en_US';

  const normalizedStructuredData = (() => {
    if (!structuredData) return null;
    const withContext = (item) => {
      if (!item || typeof item !== 'object') return null;
      if (typeof item['@context'] === 'string') return item;
      return { '@context': 'https://schema.org', ...item };
    };
    if (Array.isArray(structuredData)) {
      return structuredData.map(withContext).filter(Boolean);
    }
    return withContext(structuredData);
  })();

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang Tags for Multi-language SEO */}
      {alternateUrls.map(({ lang: hrefLang, url }) => (
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={url} />
      ))}

      {/* Open Graph Tags */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />
      {lang === 'de' && <meta property="og:locale:alternate" content="en_US" />}
      {lang === 'en' && <meta property="og:locale:alternate" content="de_DE" />}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {/* Geo Tags for Local SEO */}
      <meta name="geo.region" content="DE-BW" />
      <meta name="geo.placename" content="Freiburg im Breisgau" />
      <meta name="geo.position" content="47.9990;7.8421" />
      <meta name="ICBM" content="47.9990, 7.8421" />

      {/* Structured Data */}
      {normalizedStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(normalizedStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
